import {
    Component,
    DestroyRef,
    HostListener,
    InputSignal,
    OnInit,
    computed,
    inject,
    input,
    signal
} from '@angular/core';

import { TitleCasePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { EvAccordionComponent } from 'ev-accordion';
import { Observable, map, of, switchMap } from 'rxjs';
import { createAreaFromSearchResult, getAreaTypeNamePlural, getAreaTypeNameSingular, getParentAreaKey } from 'src/app/area-builder/area-builder.utils';
import { EvButtonComponent } from 'src/app/shared-components/ev-button/ev-button.component';
import {
    EvDropdownComponent,
    OptionsObject,
} from 'src/app/shared-components/ev-dropdown/ev-dropdown.component';
import { AreaStructure } from 'src/models/area-builder/area-structure.model';
import { AreaType } from 'src/models/area-builder/area-type.enum';
import { Area } from 'src/models/area-builder/area.model';
import { QueryArea } from 'src/models/area-builder/query-area.model';
import { IAreaSearchResultDto } from 'src/models/search/area-search-result.model';
import { AreaBuilderDataService } from 'src/services/area-builder-data.service';
import { SearchDataService } from 'src/services/search-data.service';
import { AreasStore } from 'src/stores/area-builder.store';
import { RightSidebarOptions, RightSidebarService } from 'src/services/right-sidebar.service';
import { AreaItemComponent } from './area-item/area-item.component';
import { AreaSearchItemComponent } from './area-search-item/area-search-item.component';

@Component({
    selector: 'area-builder-sidebar-v2',
    templateUrl: './area-builder-sidebar-v2.component.html',
    styleUrl: './area-builder-sidebar-v2.component.scss',
    imports: [
        AreaSearchItemComponent,
        AreaItemComponent,
        TitleCasePipe,
        ReactiveFormsModule,
        EvButtonComponent,
        EvDropdownComponent,
        EvAccordionComponent
    ]
})
export class AreaBuilderSidebarComponentV2 implements OnInit {
    myControl = new FormControl('');
    private areaBuilderDataService = inject(AreaBuilderDataService);
    private searchDataService = inject(SearchDataService);
    readonly areasStore = inject(AreasStore);
    private readonly rightSidebarService = inject(RightSidebarService);
    defaultAreaStructure: InputSignal<AreaStructure> = input.required();

    areaStructure$: Observable<AreaStructure>;
    navigatedPath: Area[] = [];
    inputText = signal<string>('');
    showCancelInput = computed(() => this.inputText().length > 0);
    searchActive = signal<boolean>(false);
    AreaType = AreaType;
    private unfilteredAreas = signal<IAreaSearchResultDto[]>(null);
    private rootQuery = { areaType: AreaType.Undefined, areaId: 0 };
    selectedFilter = signal<AreaType>(AreaType.Undefined);
    currentAreaStructure = signal<AreaStructure>(null);
    areas = computed(() => {
        return this.filterForAreaType(
            this.unfilteredAreas(),
            this.selectedFilter()
        );
    });
    filterOptions = computed(() => {
        let areas = this.unfilteredAreas();
        return [
            {
                key: AreaType.Undefined,
                displayValue: 'Alle',
                counter: this.filterForAreaType(areas, AreaType.Undefined)
                    ?.length,
            },
            {
                key: AreaType.Municipality,
                displayValue: 'Kommuner',
                counter: this.filterForAreaType(areas, AreaType.Municipality)
                    ?.length,
            },
            {
                key: AreaType.CityDistrict,
                displayValue: 'Bydeler',
                counter: this.filterForAreaType(areas, AreaType.CityDistrict)
                    ?.length,
            },
            {
                key: AreaType.SubCityDistrict,
                displayValue: 'Delbydeler',
                counter: this.filterForAreaType(areas, AreaType.SubCityDistrict)
                    ?.length,
            },
            {
                key: AreaType.BasicUnit,
                displayValue: 'Grunnkretser',
                counter: this.filterForAreaType(areas, AreaType.BasicUnit)
                    ?.length,
            },
            {
                key: AreaType.PostcodeArea,
                displayValue: 'Poststeder',
                counter: this.filterForAreaType(areas, AreaType.PostcodeArea)
                    ?.length,
            },
        ];
    });

    private styles = {'top.px': 60, 'right.px': 20, 'height': 'auto', 'background': 'rgba(0, 0, 0, 0.40)', 'border-radius': '8px'};
    private mobileStyles = {'background': 'rgba(0, 0, 0, 0.40)', 'border-radius': '8px'};
    dynamicStyles = {};

    @HostListener('window:resize', ['$event'])
    onResize(event: Event) {
      const screenWidth = (event.target as Window).innerWidth;
        // Mobile
      if (screenWidth <= 767) {
        this.dynamicStyles = {...this.mobileStyles};
      } else {
        // Desktop
        this.dynamicStyles = {...this.styles};
      }
    }

    constructor() {
        this.myControl.valueChanges
            .pipe(
                takeUntilDestroyed(inject(DestroyRef)),
                switchMap((x) => this.getAreasFromSearchString(x.replaceAll("#", "")))
            )
            .subscribe((areas) => {
                if (areas === null || areas.length === 0)
                    this.unfilteredAreas.set(null);
                else {
                    this.unfilteredAreas.set(areas);
                }
            });
    }

    setFilter(selected: OptionsObject) {
        this.selectedFilter.set(selected.key as AreaType);
    }
    resetText() {
        this.inputText.set('');
    }

    private filterForAreaType(
        areas: IAreaSearchResultDto[],
        areaType: AreaType
    ): IAreaSearchResultDto[] {
        if (areaType === AreaType.Undefined) return areas;
        if (areas === null) return null;
        return areas.filter((x) => x.areaTypeId === areaType);
    }
    private getAreasFromSearchString(
        searchString: string
    ): Observable<IAreaSearchResultDto[]> {
        if (searchString.length < 3) {
            this.searchActive.set(false);
            this.areasStore.resetPreviousSelectedArea();
            return of(null);
        }
        if (!this.searchActive()) {
            this.areasStore.resetPreviousSelectedArea();
        }
        this.searchActive.set(true);
        return this.searchDataService.getAreasWithBorders(searchString).pipe(
            map((x) =>
                x.map((area) => ({
                    ...area,
                    areaTypeName: getAreaTypeNameSingular(area.areaTypeId),
                }))
            )
        );
    }

    ngOnInit(): void {
        this.currentAreaStructure.set(this.defaultAreaStructure());
        // Initial check for screen size
        this.onResize({ target: window } as any);
    }

    fetchAreaStructure(query: QueryArea) {
        this.areaStructure$ =
            this.areaBuilderDataService.getAreaStructure(query);
        this.areaStructure$.subscribe(x => this.currentAreaStructure.set(x));
    }

    navigateToRoot() {
        this.navigatedPath = [];
        this.fetchAreaStructure(this.rootQuery);
    }

    navigateBack() {
        if (this.navigatedPath.length > 0) {
            this.navigatedPath.pop();
            const area = this.navigatedPath[this.navigatedPath.length - 1];
            this.fetchAreaStructure(
                this.navigatedPath.length
                    ? { areaType: area.areaType, areaId: area.areaId }
                    : this.rootQuery
            );
        }
    }

    navigateToArea(area: Area) {
        const idx = this.navigatedPath.findIndex(
            (a) => a.areaId == area.areaId && a.areaType == area.areaType
        );
        if (idx > -1) {
            this.navigatedPath.splice(idx + 1);
            this.fetchAreaStructure(
                this.navigatedPath.length
                    ? { areaType: area.areaType, areaId: area.areaId }
                    : this.rootQuery
            );
        }
    }

    navigateToChild(area: Area) {
        this.navigatedPath.push(area);
        this.fetchAreaStructure({
            areaType: area.areaType,
            areaId: area.areaId,
        });
    }

    shiftSelectInSearch(area: IAreaSearchResultDto) {
        const remove: boolean = this.areasStore.selectedAreas().some((x: Area) => x.areaId === area.areaId && x.areaType == area.areaTypeId);
        const currentIndex = this.areas().findIndex(x => x.areaId === area.areaId);
        const previousArea = this.areasStore.getPreviousSelectedArea();
        if (remove && this.areasStore.selectedAreas().some((x: Area) => x.areaId === previousArea.areaId && x.areaType === previousArea.areaType)) {
            return;
        } else if (!remove && !this.areasStore.selectedAreas().some((x: Area) => x.areaId === previousArea.areaId && x.areaType === previousArea.areaType)) {
            return;
        }
        const previousIndex = this.areas().findIndex(x => x.areaId === previousArea.areaId);
        if (currentIndex >= 0 && previousIndex >= 0) {
            let indexFirst = currentIndex;
            let indexLast = previousIndex;
            if (currentIndex > previousIndex) {
                indexFirst = previousIndex;
                indexLast = currentIndex;
            }
            for (let i = indexFirst + 1; i < indexLast; i++) {
                const parentKey = getParentAreaKey(this.areas()[i].areaId, this.areas()[i].areaTypeId);
                const createdArea = createAreaFromSearchResult(this.areas()[i]);
                if (remove) {
                    this.areasStore.removeSelectedArea(createdArea, parentKey);
                } else {
                    this.areasStore.addSelectedArea(createdArea, parentKey);
                }
            }
        }
    }

    shiftSelect(area: Area) {
        const remove: boolean = this.areasStore.selectedAreas().some((x: Area) => x.areaId === area.areaId && x.areaType === area.areaType);
        const currentListIndex = this.currentAreaStructure().areas.findIndex(x => x[0].areaType === area.areaType);
        if (currentListIndex >= 0) {
            const currentAreaList = this.currentAreaStructure().areas[currentListIndex];
            const indexCurrent = currentAreaList.findIndex(x => x.areaId === area.areaId);
            const previousArea = this.areasStore.getPreviousSelectedArea();
            if (remove && this.areasStore.selectedAreas().some((x: Area) => x.areaId === previousArea.areaId && x.areaType === previousArea.areaType)) {
                return;
            } else if (!remove && !this.areasStore.selectedAreas().some((x: Area) => x.areaId === previousArea.areaId && x.areaType === previousArea.areaType)) {
                return;
            }
            const previousAreaListIndex = this.currentAreaStructure().areas.findIndex(x => x[0].areaType === previousArea.areaType);
            if (previousAreaListIndex >= 0) {
                const previousAreaList = this.currentAreaStructure().areas[previousAreaListIndex];
                const indexPrevious = previousAreaList.findIndex(x => x.areaId === previousArea.areaId);

                let indexFirstList = currentListIndex;
                let indexLastList = previousAreaListIndex;
                let indexInFirstList = indexCurrent;
                let indexInLastList = indexPrevious;
                if (previousAreaListIndex < currentListIndex || (previousAreaListIndex === currentListIndex && indexPrevious < indexCurrent)) {
                    indexFirstList = previousAreaListIndex;
                    indexLastList = currentListIndex;
                    indexInFirstList = indexPrevious;
                    indexInLastList = indexCurrent;
                }

                for (let i = indexFirstList; i <= indexLastList; i++) {
                    let workingList = this.currentAreaStructure().areas[i];
                    let startIndex = i === indexFirstList ? indexInFirstList+1 : 0;
                    let finishIndex = i === indexLastList ? indexInLastList : workingList.length;
                    for (let j = startIndex; j < finishIndex; j++) {
                        const parentKey = this.getParentKey(workingList[j])
                        if (remove) {
                            this.areasStore.removeSelectedArea(workingList[j], parentKey)
                        } else {
                            this.areasStore.addSelectedArea(workingList[j], parentKey);
                        }
                    }
                }
            }
        }
    }

    private getParentKey(area: Area | IAreaSearchResultDto): string {
        let parentAreaId;
        let parentAreaType;
        if (area.areaType === AreaType.County) {
            parentAreaId = 0;
            parentAreaType = AreaType.Undefined; // Vil da egentlig være Country som parent
        } else {
            const parent = this.navigatedPath[this.navigatedPath.length - 1]
            parentAreaId = parent.areaId;
            parentAreaType = parent.areaType;
        }
        return getParentAreaKey(parentAreaId, parentAreaType);
    }

    closeSidebar(): void {
        this.rightSidebarService.closeSidebar();
    }

    trackByArea(_: number, item: Area) {
        return `${AreaType[item.areaType].toString()}-${item.areaId}`;
    }

    getPathName(area: Area) {
        if (!area || area.areaType === AreaType.County) return '';
        return getAreaTypeNamePlural(area.areaType);
    }
}
